import React from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { StoreShape } from 'hellospa/redux/types';
import {
  UserFile, SignerFile, UserFileTypes, FilePollResponseStatus,
} from 'hellospa/page/prep-and-send/data/types/file';
import { deleteFile, deleteTemplateFiles } from 'hellospa/page/prep-and-send/data/actions/file';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import styles from './styles.scss';
import DocumentIcon from 'hellospa/icons/document-icon';
import TemplateIcon from 'hellospa/icons/template-icon';
import ErrorTriangleIcon from 'hellospa/components/notification-banner/icons/error-triangle';
import SignerIcon from 'hellospa/icons/signer-group-icon';
import deleteIcon from './assets/delete-icon.svg';
import DeleteTemplateModal from './delete-template-modal';
import DocumentPassswordModal from '../document-password-modal';
import { DragHandle } from 'hellospa/components/drag-sort';
import { deleteSignerCSV } from 'hellospa/page/prep-and-send/data/actions/bulk-send';
import getFileErrorMessage from './messages';
import ProgressIndicator from 'hellospa/foundation/control/progress-indicator';

type Props = {
  file: UserFile | SignerFile,
};

type DeleteFileIconProps = {
  onPrepareDelete: () => void,
  allowFileDelete: boolean,
}
const DeleteFileIcon = ({ onPrepareDelete, allowFileDelete }: DeleteFileIconProps) => {
  if (!allowFileDelete) {
    return null;
  }
  return <a
    className={styles.deleteFileIcon}
    onClick={onPrepareDelete}
    data-qa-ref="remove-document"
  >
    <img src={deleteIcon} />
  </a>;
};

export default function FileStatus({ file }: Props) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const templateFiles = useSelector((state: StoreShape) => {
    if (file.type !== UserFileTypes.Signer) {
      return selectors.getFilesByTemplateGuid(state, file.templateGuid);
    }
    return [];
  });
  const allowFileDelete = useSelector(
    (state: StoreShape) => selectors.allowFileDeletion(state, file),
  );
  const [templateGuid, setTemplateGuid] = React.useState<string | null>(null);
  const isDeepIntegration = useSelector(selectors.isDeepIntegration);
  const isHelloFax = useSelector(selectors.isHelloFax);
  const onSingleDocumentDelete = React.useCallback((): void => {
    try {
      if (file.type !== UserFileTypes.Signer) {
        dispatch(deleteFile(file.rootSnapshotGuid));
      } else {
        dispatch(deleteSignerCSV());
      }
    } catch (e) {
      throw new Error('Encountered an error when deleting a file!');
    }
  }, [dispatch, file]);

  const onPrepareDelete = React.useCallback(() => {
    // if file isn't a template file, continue and delete it
    if (file.type !== UserFileTypes.Template) {
      onSingleDocumentDelete();
      return;
    }
    const templateFile = file;
    if (!templateFile.templateGuid) {
      throw new Error('Template guid is missing from template file!');
    }

    // if there's only 1 template file continue deleting
    // without triggering the delete template modal
    if (templateFiles.length === 1) {
      dispatch(deleteTemplateFiles(templateFile.templateGuid));
      return;
    }

    // show the modal if more than 1 template file
    setTemplateGuid(templateFile.templateGuid);
  }, [dispatch, onSingleDocumentDelete, file, templateFiles]);

  const onDeleteModalClose = React.useCallback(() => {
    setTemplateGuid(null);
  }, []);

  const onTemplateDelete = React.useCallback(() => {
    if ('templateGuid' in file) {
      if (file.type !== UserFileTypes.Template) {
        throw new Error('File is not a template file!');
      }

      try {
        dispatch(deleteTemplateFiles(file.templateGuid));
        onDeleteModalClose();
      } catch (e) {
        throw new Error('Encountered an error when deleting template files!');
      }
    }
  }, [dispatch, file, onDeleteModalClose]);

  let content;

  if (file.status === FilePollResponseStatus.Ok) {
    let FileIcon = DocumentIcon;
    switch (file.type) {
      case UserFileTypes.Signer:
        FileIcon = SignerIcon;
        break;
      case UserFileTypes.Template:
        FileIcon = TemplateIcon;
        break;
      default:
    }

    content = (
      <div className={styles.content}>
        {'rootSnapshotGuid' in file && <DragHandle className={styles.dragIcon} data-qa-ref="drag-document" />}

        <FileIcon
          data-qa-ref='file-icon'
          className={styles.fileIcon} />
        <span data-type={file.type} className={styles.filename}>{file.name}</span>
      </div>
    );
  } else if (file.status === FilePollResponseStatus.Converting) {
    const originalTotalPages = ('total' in file && file.total) || 0;
    // default to 1, to prevent division by 0
    const totalPages = originalTotalPages || 1;
    const fileProgress = file.progress || 0;


    let percent = 10;
    if (file.status === FilePollResponseStatus.Converting) {
      // for some reason, for docx files, backend responds with a total of 0,
      // but increments the progress, so let's just keep it at 10%
      if (originalTotalPages === 0) {
        percent = 10;
      } else if (fileProgress <= totalPages) {
        // if no file progress, start with a 10% to make it look like it's doing something
        percent = ((fileProgress / totalPages) * 90) + 10;
      }
    } else if (file.status === FilePollResponseStatus.Ok) { // if ok, it's converted, set it to 100%
      percent = 100;
    }
    const convertText = file.type === UserFileTypes.External
      ? <FormattedMessage
        id=""
        description="text for file being retrieved from integration"
        defaultMessage="Retrieving" />
      : <FormattedMessage
        id=""
        description="text for file being uploaded from local"
        defaultMessage="Uploading" />;

    content = (
      <React.Fragment>
        <ProgressIndicator
          className={styles.progress}
          showCancelButton={false}
          showLoadingMessage={false}
          progress={percent / 100} />
        <div className={styles.progressBarText}>
          {convertText} {file.name}...
        </div>
      </React.Fragment>
    );
  } else {
    const message = getFileErrorMessage(file.status, {
      useLong: isDeepIntegration,
      isHelloFax,
    });

    content = (
      <div className={classNames(styles.content, styles.error)}>
        <ErrorTriangleIcon className={styles.errorTriangle} />
        <span className={styles.filename}>
          {file.name}
        </span>
        &nbsp;
        <span>
          <FormattedMessage
            id=""
            description="text to show before the upload error"
            defaultMessage="Upload failed." />
        </span>
        &nbsp;
        <span className={styles.noWrap}>
          {intl.formatMessage(message.descriptor, message.values)}
        </span>
      </div>
    );
  }

  return (

    <div>
      {'rootSnapshotGuid' in file && <DocumentPassswordModal file={file} />}
      {templateGuid && (
        <DeleteTemplateModal
          isOpen={true}
          files={templateFiles}
          onClose={onDeleteModalClose}
          onDelete={onTemplateDelete}
          templateGuid={templateGuid} />
      )}

      <div className={classNames(styles.fileContainer, {
        [styles.converting]: file.status === FilePollResponseStatus.Converting,
      })}
      >
        {content}

        <DeleteFileIcon
          onPrepareDelete={onPrepareDelete}
          allowFileDelete={allowFileDelete} />
      </div>
    </div>
  );
}
